import { ARCADE_FPS, clamp, progress1000 } from './common.js';
import type { ArcadeBaseState, ArcadeSimulation } from './common.js';
import { ArcadeRng } from './rng.js';

/**
 * Kopi Catch: slide a tray along the counter to catch falling kopi, teh and
 * kaya toast. Flies cost points, durians cost a life. Catch streaks raise a
 * multiplier; everything falls faster as the rush goes on.
 */

export const KC_WIDTH = 160;
export const KC_HEIGHT = 200;
export const KC_TRAY_Y = 182;
export const KC_TRAY_HALF = 14;
export const KC_FRAMES = 75 * ARCADE_FPS;
export const KC_LIVES = 3;

/** Input codes. 10..170 steer toward x = code - 10 (touch/drag). */
export const KC_LEFT_DOWN = 1;
export const KC_LEFT_UP = 2;
export const KC_RIGHT_DOWN = 3;
export const KC_RIGHT_UP = 4;
export const KC_TOUCH_UP = 5;
export const KC_TARGET_BASE = 10;

export type KcItemType = 'kopi' | 'teh' | 'toast' | 'golden' | 'fly' | 'durian';

const POINTS: Record<KcItemType, number> = { kopi: 10, teh: 10, toast: 15, golden: 50, fly: 0, durian: 0 };

export interface KcItem {
  id: number;
  type: KcItemType;
  x: number;
  /** Fixed point, 1/256 px. */
  y: number;
  vy: number;
}

export interface KopiCatchState extends ArcadeBaseState {
  rng: ArcadeRng;
  lives: number;
  trayX: number;
  left: boolean;
  right: boolean;
  target: number | null;
  items: KcItem[];
  nextSpawn: number;
  nextId: number;
  combo: number;
  multiplier: number;
  caught: number;
}

export const kopiCatch: ArcadeSimulation<KopiCatchState> = {
  maxFrames: KC_FRAMES,

  create(seed) {
    return {
      frame: 0,
      over: false,
      score: 0,
      fx: [],
      rng: new ArcadeRng(seed),
      lives: KC_LIVES,
      trayX: KC_WIDTH / 2,
      left: false,
      right: false,
      target: null,
      items: [],
      nextSpawn: 30,
      nextId: 1,
      combo: 0,
      multiplier: 1,
      caught: 0,
    };
  },

  validCode(code) {
    return (code >= 1 && code <= 5) || (code >= KC_TARGET_BASE && code <= KC_TARGET_BASE + KC_WIDTH);
  },

  step(s, codes) {
    if (s.over) return;
    s.fx = [];
    for (const code of codes) {
      if (code === KC_LEFT_DOWN) s.left = true;
      else if (code === KC_LEFT_UP) s.left = false;
      else if (code === KC_RIGHT_DOWN) s.right = true;
      else if (code === KC_RIGHT_UP) s.right = false;
      else if (code === KC_TOUCH_UP) s.target = null;
      else if (code >= KC_TARGET_BASE) s.target = code - KC_TARGET_BASE;
      if (code >= 1 && code <= 4) s.target = null;
    }

    // Tray: keyboard nudges 3 px a frame; a touch target is chased at 4.
    if (s.target !== null) {
      const delta = clamp(s.target - s.trayX, -4, 4);
      s.trayX += delta;
    } else {
      s.trayX += ((s.right ? 1 : 0) - (s.left ? 1 : 0)) * 3;
    }
    s.trayX = clamp(s.trayX, KC_TRAY_HALF, KC_WIDTH - KC_TRAY_HALF);

    const p = progress1000(s.frame, KC_FRAMES);
    if (s.frame >= s.nextSpawn) {
      const roll = s.rng.int(0, 100);
      // Hazards get a little more common as the rush builds.
      const durianCut = 8 + Math.floor((p * 6) / 1000);
      const flyCut = durianCut + 12;
      const type: KcItemType =
        roll < 3 ? 'golden' : roll < 3 + durianCut ? 'durian' : roll < 3 + flyCut ? 'fly' : roll < 45 ? 'toast' : roll < 72 ? 'teh' : 'kopi';
      s.items.push({
        id: s.nextId++,
        type,
        x: s.rng.int(8, KC_WIDTH - 7),
        y: -8 * 256,
        vy: 180 + Math.floor((p * 260) / 1000) + s.rng.int(0, 60),
      });
      s.nextSpawn = s.frame + 42 - Math.floor((p * 26) / 1000) + s.rng.int(0, 10);
    }

    const kept: KcItem[] = [];
    for (const item of s.items) {
      item.y += item.vy;
      const y = item.y >> 8;
      const onTray = y >= KC_TRAY_Y - 6 && y <= KC_TRAY_Y + 2 && Math.abs(item.x - s.trayX) <= KC_TRAY_HALF + 3;
      if (onTray) {
        catchItem(s, item);
        continue;
      }
      if (y > KC_HEIGHT + 8) {
        if (POINTS[item.type] > 0) {
          s.combo = 0;
          s.multiplier = 1;
          s.fx.push({ kind: 'miss', x: item.x, y: KC_HEIGHT });
        }
        continue;
      }
      kept.push(item);
    }
    s.items = kept;

    s.frame += 1;
    if (s.frame >= KC_FRAMES || s.lives <= 0) s.over = true;
  },
};

function catchItem(s: KopiCatchState, item: KcItem): void {
  if (item.type === 'durian') {
    s.lives -= 1;
    s.combo = 0;
    s.multiplier = 1;
    s.fx.push({ kind: 'hurt', x: item.x, y: KC_TRAY_Y });
    return;
  }
  if (item.type === 'fly') {
    s.score = Math.max(0, s.score - 5);
    s.combo = 0;
    s.multiplier = 1;
    s.fx.push({ kind: 'bad', x: item.x, y: KC_TRAY_Y, value: -5 });
    return;
  }
  const points = POINTS[item.type] * s.multiplier;
  s.score += points;
  s.caught += 1;
  s.combo += 1;
  s.multiplier = Math.min(5, 1 + Math.floor(s.combo / 5));
  s.fx.push({ kind: item.type === 'golden' ? 'golden' : 'catch', x: item.x, y: KC_TRAY_Y, value: points });
}
